<template>
  <div class="section" id="orders">
    <div class="container">
      <h1 class="h1">Заказы</h1>
      <div class="content">
        <OrdersSearch
          v-model="searchValue"
          @input="search"
        />
        <div class="w-tabs">
          <div class="table-tabs w-tab-menu">
            <div
              v-for="tab in orders"
              :key="tab.slug"
              :class="{'w--current': activeTab && activeTab.slug === tab.slug}"
              class="table-tab w-inline-block w-tab-link"
              @click="changeTab(tab)"
            >
              <div>{{ tab.name }} <span class="count">{{ counters[tab.slug] }}</span></div>
            </div>
          </div>

          <div class="tabs-content w-tab-content">
            <Spinner v-if="isLoading" size="huge" />

            <transition name="fade">
              <div v-if="!isLoading && activeTab" class="w-tab-pane w--tab-active">
                <NoOrdersFound v-if="activeTab.count === 0" :tab="activeTab.slug" />
                <OrdersTable v-else :orders="activeTab.orders" :tab="activeTab.slug" />
              </div>
            </transition>
            <div class="load-trigger" >
              <Spinner v-if="isLoadingMore" size="medium" class="medium-spinner"/>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import {mapState, mapActions} from 'vuex';
  import OrdersSearch from '@/containers/orders/Search';
  import parseError from '@/utils/api/parse-error';
  import factory from '@/utils/api/factory';
  import tellMe from '@/utils/accessStrings';
  import Spinner from 'vue-simple-spinner';
  import getTabs from '@/utils/constants/order-tabs';
  import NoOrdersFound from '@/containers/orders/NotFound';
  import OrdersTable from '@/containers/orders/Table';
  import request from '@/utils/api/request';

  export default {
    name: 'Orders',
    components: {
      OrdersTable,
      NoOrdersFound,
      OrdersSearch,
      Spinner,
    },
    data () {
      return {
        isLoading: false,
        isLoadingMore: false,
        error: null,
        allOrders: [],
        orders: {},
        activeTab: null,
        searchValue: '',
        nextOrders: null,
        counters: {},
      };
    },
    computed: mapState({
      action: state => state.global.action,
      role: state => state.auth?.user?.role,
    }),
    async created() {
      await this.loadCounters(this.role);
      if (this.role === 'client') this.load(false, 'active');
      else if (this.role === 'lab_employee') this.load(false, 'in_progress');
      else this.load(false, 'new');
    },
    mounted() {
      setInterval(() => this.load(true, this.activeTab.slug), 1000 * 30);
      const observer = new IntersectionObserver((el) => {
        if (el[0].intersectionRatio > 0) {
          this.loadMore();
        }
      }, {rootMargin: '200px'});
      observer.observe(document.getElementsByClassName('load-trigger')[0]);
    },
    watch: {
      async action (val) {
        if (val) {
          if (val === 'reloadOrders') {
            await this.loadCounters(this.role);
            await this.load(false, this.activeTab.slug);
          }
          this.changeAction(null);
        }
      },
    },
    methods: {
      ...mapActions({
        changeAction: 'global/changeAction',
        openModal: 'modal/openModal',
      }),
      async loadCounters (role) {
        try {
          this.isLoading = true;
          const apiCalls = [];
          if (role === 'client') {
            ['new', 'in_progress', 'completed', 'rejected'].forEach(el => {
              apiCalls.push(factory(tellMe('ordersEndpoint')).indexInPath('orders', {limit: 1, status: el}));
            });
            const [received, in_progress, completed, rejected] = await Promise.all(apiCalls);
            this.counters = {active: received.data.count + in_progress.data.count, completed: completed.data.count, rejected: rejected.data.count};
          } else if (role === 'lab_employee') {
            ['in_progress', 'completed'].forEach(el => {
              apiCalls.push(factory(tellMe('ordersEndpoint')).indexInPath('orders', {limit: 1, status: el}));
            });

            const [in_progress, completed] = await Promise.all(apiCalls);

            this.counters = {in_progress: in_progress.data.count, completed: completed.data.count};
          } else {
            ['new', 'in_progress', 'completed', 'rejected'].forEach(el => {
              apiCalls.push(factory(tellMe('ordersEndpoint')).indexInPath('orders', {limit: 1, status: el}));
            });

            const [received, in_progress, completed, rejected] = await Promise.all(apiCalls);

            this.counters = {
              new: received.data.count,
              in_progress: in_progress.data.count,
              completed: completed.data.count,
              rejected: rejected.data.count,
            };
          }
          this.isLoading = false;
        } catch (e) {
          this.error = parseError(e) || 'Что-то пошло не так.';
          this.isLoading = false;
        }
      },
      async load (silentLoad = false, tab) {
        try {
          if (!silentLoad) this.isLoading = true;
          let tabToRequest = '';
          if (tab === 'active') tabToRequest = 'new,in_progress';
          else tabToRequest = tab;

          let params = {};
          if (!silentLoad) params = {limit: 15, order: 'id', status: tabToRequest};
          else params = {limit: this.orders[this.activeTab.slug].count, order: 'id', status: tabToRequest};

          const {data} = await factory(tellMe('ordersEndpoint')).indexInPath('orders', params);

          if (!silentLoad) this.nextOrders = data.next;
          this.allOrders = data.results;
          const tabs = await getTabs(data.results);
          this.orders = tabs;

          if (!silentLoad && tab !== 'active') this.activeTab = tabs[tab];
          else if (tab === 'active') this.activeTab = tabs.active;
          this.isLoading = false;
        } catch (e) {
          this.error = parseError(e) || 'Что-то пошло не так.';
          this.isLoading = false;
        }
      },
      // TODO: Fix the inconsistency
      async loadMore () {
        if (this.nextOrders) {
          try {
            this.isLoadingMore = true;
            const {data} = await request(this.nextOrders);
            this.nextOrders = data.next;
            this.allOrders = this.allOrders.concat(data.results);
            const tabs = await getTabs(data.results);
            this.orders[this.activeTab.slug].count += tabs[this.activeTab.slug].count;
            this.orders[this.activeTab.slug].orders = this.orders[this.activeTab.slug].orders.concat(tabs[this.activeTab.slug].orders);
            if (this.orders[this.activeTab.slug].orders !== this.activeTab.orders) {
              this.activeTab.orders = this.activeTab.orders.concat(tabs[this.activeTab.slug].orders);
            }
            this.isLoadingMore = false;
          } catch (e) {
            this.error = parseError(e) || 'Что-то пошло не так.';
            this.isLoading = false;
          }
        }
      },
      async search () {
        this.isLoading = true;
        const {data} = await factory(tellMe('ordersEndpoint'))
          .indexInPath('orders',
                       {
                         limit: 15,
                         search: this.searchValue.toLowerCase(),
                         status: this.activeTab.slug === 'active' ? ['new', 'in_progress'] : this.activeTab.slug,
                       });
        this.nextOrders = data.next;
        this.allOrders = data.results;
        const tabs = await getTabs(data.results);
        this.orders = tabs;
        this.activeTab = tabs[this.activeTab.slug];
        this.isLoading = false;
      },
      changeTab (tab) {
        if (this.activeTab === tab) {
          return;
        }
        this.load(false, tab.slug);
      },
    },
  };
</script>

<style lang="scss">
  #orders {
    .table-header .table-item {
      background: #F9FAFC !important;
    }
  }
</style>

<style lang="scss">
  // EMBEDS
  .hint{
    display: none;
    opacity: 0;
  }

  #orders {
    .file-title, .patient-title, .reason, .employee-title{
      text-overflow: ellipsis;
    }

    .reason-wrap:hover .hint{
      display: block;
      animation: hint 200ms ease-out forwards;
    }

    .cancel-application:hover .hint{
      display: block;
      animation: hint 200ms ease-out forwards;
    }

    @keyframes hint {
      to {
        opacity: 1;
        transform: translate(0, 4px);
      }
    }

    .file-title, .patient-title, .reason{
      text-overflow: ellipsis;
    }

    .table-drop, .table-toggle, .table-header, .order-details{
      display: contents;
    }

    .table-toggle.table-toggle-highlight .table-item{
      background: rgba(255, 213, 62, 0.16);

    }

    .table-toggle .table-item:first-child{
      border-radius: 8px 0 0 8px;

    }

    .table-toggle .table-item:last-child{
      border-radius: 0 8px 8px 0;

    }

    .order-details .table-item{
      border-radius: 0px !important;
    }

    .results-table-droplist{
      grid-area: auto/1/ auto / span 7;
    }

    .table-search.table-search-done .results-table-droplist{
      grid-area: auto/1/ auto / span 6;
    }

    .table-search.table-search-clinic-work .results-table-droplist{
      grid-area: auto/1/ auto / span 8;
    }

    .table-header .table-item{
      background: rgba(255,255,255, .5);
      border-radius: 0;
      text-transform: uppercase;
      color: #a0a5ba;
      font-size: 12px;
      position: sticky;
      letter-spacing: 1px;
      top: 0;
      z-index: 120;
      padding: 8px 20px;
    }

    .table-header .table-item:first-child{
      border-radius: 8px 0 0 8px;
    }

    .table-header .table-item:last-child{
      border-radius: 0 8px 8px 0;
    }

    @media screen and (max-width: 991px) {
      .table-drop{
        display: block;
      }

      .results-table-droplist, .banner-2{
        grid-area: auto/1/ auto / span 2;
      }

      .order-details{
        display: flex;
      }

      .main-info{
        display: block;
      }
      .table-toggle.table-toggle-highlight .table-item{
        background: transparent;
      }

      .table-toggle{
        display: flex;
      }

      .table-header{
        display: none;
      }
    }

    .load-trigger {
      height: 100px;

      .medium-spinner {
        padding-top: 30px;
      }
    }

    @media screen and (max-width: 479px) {
      .table-toggle{
        flex-direction: column;

      }
    }
  }
</style>
